import styled from 'styled-components'
import React, { useEffect, useRef, useState } from 'react'
import { theme } from '../../utils/GlobalStyles'
import DatePicker from 'react-datepicker'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ReactComponent as WithdrawIcon } from '../../assets/icons/withdraws.svg'
import { ReactComponent as LBTIcon } from 'assets/icons/lbtCoin.svg'

import {
  DatePickerButton,
  DatePickerName,
  DatePickerWrapper,
  FailedWrapper,
  FilterButtonsContainer,
  FilterWrapper,
  TimeRange,
  TimeRangeWrapper,
  TimeWrapper,
} from './Actions'
import { timerByFilter, timeToTz } from './utils'
import { useGetInvestChartQuery } from '../../redux/rtkApi/chartsApi'
import { ColorType, createChart, IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts'
import { LegendBlock, LegendRow, LegendTitle } from './Charts/Charts.styled'

export const LBTChart: React.FC<{ lbtPrice: number; backups: number; lastProfit: number }> = ({ lbtPrice, backups, lastProfit }) => {
  const [isLast24H, setIsLast24H] = useState(false)
  const [isLastHour, setIsLastHour] = useState(false)
  const [showAll, setShowAll] = useState(true)
  const [startTime, setStartTime] = useState(Date.now() - 1000 * 60 * 60 * 24 * 10)
  const [endTime, setEndTime] = useState(Date.now())
  const [showFilterByDate, setShowFilterByDate] = useState(false)
  const [somePropChanged, setSomePropChanged] = useState(false)
  const [allCharts, setAllCharts] = useState<Map<keyof InvestChartTypes, ISeriesApi<'Line'>>>(new Map())

  const { data = [], isLoading } = useGetInvestChartQuery(
    {
      showAll,
      last24h: isLast24H,
      lastHour: isLastHour,
      startTime,
      endTime,
    },
    {
      pollingInterval: 5000,
      refetchOnMountOrArgChange: true,
    }
  )

  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chart = useRef<IChartApi>(undefined)

  useEffect(() => {
    setSomePropChanged(true)
  }, [isLast24H, isLastHour, showAll])
  useEffect(() => {
    if (!chart?.current) {
      chart.current = createChart(chartContainerRef.current || '', {
        layout: {
          background: { type: ColorType.Solid, color: theme.dark },
          textColor: '#ffffff',
        },
        grid: {
          vertLines: { color: theme.white11 },
          horzLines: { color: theme.white11 },
        },
        height: 300,
        width: chartContainerRef?.current?.clientWidth,
        crosshair: {
          vertLine: {
            labelBackgroundColor: theme.yellow,
          },

          horzLine: {
            labelBackgroundColor: theme.yellow,
          },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: true,
        },
      })
    }
    const handleResize = () => {
      chart?.current?.applyOptions({
        width: chartContainerRef?.current?.clientWidth,
      })
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  useEffect(() => {
    const key = 'LBTPrice'
    if (allCharts.has(key as keyof InvestChartTypes)) {
      const exists = allCharts.get(key as keyof InvestChartTypes)
      if (exists) {
        exists.setData(
          [...data]
            .sort((prev, next) => prev.date - next.date)
            .map(timeInterval => ({
              time: timeToTz(timeInterval.date) as UTCTimestamp,
              // time: Math.floor(timeInterval.date / 1000) as UTCTimestamp,
              value: timeInterval.LBT_price,
            }))
        )
      }
    } else {
      const lineSeries = chart?.current?.addLineSeries({
        color: theme.blue,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      if (lineSeries) allCharts.set(key as keyof InvestChartTypes, lineSeries)
      lineSeries?.setData(
        [...data]
          .sort((prev, next) => prev.date - next.date)
          .map(timeInterval => ({
            time: timeToTz(timeInterval.date) as UTCTimestamp,
            // time: Math.floor(timeInterval.date / 1000) as UTCTimestamp,
            value: timeInterval.LBT_price,
          }))
      )
    }

    if (somePropChanged && !isLoading) {
      chart?.current?.timeScale().fitContent()
      setSomePropChanged(false)
    }
  }, [data, isLast24H, isLastHour, showAll])
  return (
    <Wrapper>
      <FilterType>
        <LeftBlock>
          <TimeRangeWrapper>
            <TimeWrapper style={{ marginRight: '10px' }}>
              {' '}
              {isLastHour || isLast24H ? (
                <TimeRange> {timerByFilter({ isLastHour, isLast24H, timeIndex: 0 })}</TimeRange>
              ) : (
                <TimeRange>(08/05/2022 - present)</TimeRange>
              )}
            </TimeWrapper>

            <FilterButtonsContainer>
              <DatePickerButton
                isActive={showAll}
                onClick={() => {
                  setShowAll(true)
                  setIsLast24H(false)
                  setIsLastHour(false)
                }}
              >
                ALL
              </DatePickerButton>
              <DatePickerButton
                isActive={isLast24H}
                onClick={() => {
                  setShowAll(false)
                  setIsLast24H(true)
                  setIsLastHour(false)
                }}
                data-title={'By last 24 hour'}
              >
                24H
              </DatePickerButton>
              {/*<DatePickerButton*/}
              {/*  isActive={isLastHour}*/}
              {/*  onClick={() => {*/}
              {/*    setShowAll(false)*/}
              {/*    setIsLast24H(false)*/}
              {/*    setIsLastHour(true)*/}
              {/*  }}*/}
              {/*>*/}
              {/*  1H*/}
              {/*</DatePickerButton>*/}
              <FailedWrapper
                onMouseOver={() => setShowFilterByDate(true)}
                onMouseLeave={() => setShowFilterByDate(false)}
                style={{
                  marginLeft: '10px',
                  width: '30px',
                }}
                data-title={'By last hour'}
              >
                <CalendarIcon
                  style={{
                    fill: !showAll && !isLast24H && !isLastHour ? theme.blue : theme.white33,
                    width: '15px',
                    height: '15px',
                    marginLeft: '5px',
                    position: 'absolute',
                    top: '-1px',
                  }}
                />
                {showFilterByDate && (
                  <div>
                    <FilterWrapper style={{ top: '14px' }}>
                      <DatePickerWrapper>
                        <DatePickerName>From</DatePickerName>
                        <DatePicker
                          showTimeSelect
                          showTimeInput
                          dateFormat="dd/MM/yyyy HH:mm:ss"
                          selected={new Date(startTime)}
                          onChange={date => {
                            setShowAll(false)
                            setIsLast24H(false)
                            setIsLastHour(false)
                            setStartTime(new Date(date as Date).getTime())
                          }}
                          popperPlacement="bottom-end"
                        />
                      </DatePickerWrapper>

                      <DatePickerWrapper>
                        <DatePickerName>To</DatePickerName>
                        <DatePicker
                          showTimeSelect
                          showTimeInput
                          dateFormat="dd/MM/yyyy HH:mm:ss"
                          selected={new Date(endTime)}
                          onChange={date => {
                            setShowAll(false)
                            setIsLast24H(false)
                            setIsLastHour(false)
                            setEndTime(new Date(date as Date).getTime())
                          }}
                          popperPlacement="bottom-end"
                        />
                      </DatePickerWrapper>
                    </FilterWrapper>
                  </div>
                )}
              </FailedWrapper>
            </FilterButtonsContainer>
          </TimeRangeWrapper>
        </LeftBlock>
        <RightBlock>
          <LbtHeaderPrice>
            <SvgHeadIconWrapper>
              <LBTIcon />
            </SvgHeadIconWrapper>
            : {lbtPrice.toFixed(2)}$
          </LbtHeaderPrice>
          <div style={{ display: 'flex', alignItems: 'center', height: '19px' }}>
            <WithdrawIcon
              style={{
                fill: theme.white99,
                width: '10px',
                height: '10px',
                marginRight: '5px',
                marginLeft: '10px',
              }}
            />
            : <ColoredSpan color={backups && backups > 0 ? 'red' : 'green'}>{backups}</ColoredSpan>
          </div>
          <LastProfit profit={lastProfit}>{lastProfit.toFixed(2)}$</LastProfit>
        </RightBlock>
      </FilterType>
      <ChartBlock ref={chartContainerRef}>
        <LegendBlock>
          <LegendRow>
            <LegendTitle colorByKey={theme.blue}>LBT Price</LegendTitle>
          </LegendRow>
        </LegendBlock>
      </ChartBlock>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: 10px 1% 0;
`

export interface InvestChartTypes {
  LBTPrice: number
}

export const ChartBlock = styled.div`
  position: relative;
  margin: 10px auto;
  width: 100%;
  height: 300px;
  z-index: 0;
`
export const LastProfit = styled.div<{ profit: number }>`
  width: 70px;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ profit, theme }) => (profit >= 0 ? theme.green : theme.red)};
  font-weight: normal;
  &:before {
    content: '+';
    display: ${({ profit }) => (profit > 0 ? 'inline' : 'none')};
  }
`
const ColoredSpan = styled.span<{ color: string }>`
  color: ${({ theme, color }) => theme[color]};
  padding-left: 4px;
`

const LbtHeaderPrice = styled.div`
  margin-right: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 11px;
`

const SvgHeadIconWrapper = styled.div`
  margin-left: 4px;
  display: flex;
  align-items: center;
  svg {
    width: 16px;
    height: 16px;
  }
`
const LeftBlock = styled.div`
  display: flex;
`
const RightBlock = styled.div`
  display: flex;
  font-size: 12px;
`
export const FilterType = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
